import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, FolderDown, Loader2 } from "lucide-react";
import { readWithAliases, Keys } from "../../../lib/storage/keys";
import { storage } from "../../../lib/storage/storage";
import {
	getAvailableOfflineRoots,
	getDefaultOfflineRoot,
	getExportProducaoOfflineConfig,
	listOfflineServiceOptions,
	persistOfflineRoot,
	type OfflineServiceId,
} from "../../../utils/exportProducaoOfflineConfig";
import { buildContingencyExportPath } from "../../../utils/buildContingencyExportPath";
import { getServiceFolder } from "../../../utils/inferOfflineService";
import {
	getRecommendation,
	type ExportFormat,
} from "../../ExportModal/utils/recommendations";
import { normalizeWindowsPath } from "../../../utils/path";

export interface ExportProducaoOfflineOptions {
	format: ExportFormat;
	includeLayoutPng: boolean;
	outputDir: string;
	root: string;
	serviceId: OfflineServiceId;
	manualDir?: string;
	orcId?: number;
	oitId?: number;
}

interface DocumentInfo {
	nomeArquivo?: string;
	caminhoArquivo?: string;
}

interface ExportProducaoOfflineModalProps {
	isOpen: boolean;
	isExporting?: boolean;
	documentInfo?: DocumentInfo | null;
	documentLoading?: boolean;
	parsedOrcId?: number | null;
	parsedOitId?: number | null;
	parsedMaterial?: string | null;
	inferredServiceId?: OfflineServiceId | null;
	onClose: () => void;
	onConfirm: (options: ExportProducaoOfflineOptions) => void;
}

const FORMAT_OPTIONS: { value: ExportFormat; label: string }[] = [
	{ value: "tiff", label: "TIFF" },
	{ value: "pdf", label: "PDF" },
	{ value: "eps", label: "EPS" },
	{ value: "png", label: "PNG" },
];

export function ExportProducaoOfflineModal({
	isOpen,
	isExporting = false,
	documentInfo,
	documentLoading = false,
	parsedOrcId,
	parsedOitId,
	parsedMaterial,
	inferredServiceId,
	onClose,
	onConfirm,
}: ExportProducaoOfflineModalProps) {
	const savedData = useMemo(
		() =>
			readWithAliases<Record<string, unknown>>(Keys.export.currentSaveData, [
				"currentSaveData",
			], { area: "session" }),
		[isOpen],
	);

	const pedido = savedData?.pedido as
		| { orc_id?: number; oit_id?: number }
		| undefined;
	const produto = savedData?.produto as
		| { pro_id?: number; id?: number; nome?: string }
		| undefined;

	const config = getExportProducaoOfflineConfig();
	const serviceOptions = listOfflineServiceOptions();
	const availableRoots = getAvailableOfflineRoots();

	const defaultFormat =
		getRecommendation({
			proId: produto?.pro_id ?? produto?.id ?? null,
			productName: produto?.nome ?? parsedMaterial ?? null,
		})?.format ?? "tiff";

	const [format, setFormat] = useState<ExportFormat>(defaultFormat);
	const [includeLayoutPng, setIncludeLayoutPng] = useState(false);
	const [selectedRoot, setSelectedRoot] = useState(getDefaultOfflineRoot());
	const [serviceId, setServiceId] = useState<OfflineServiceId>(
		inferredServiceId ?? "DTF_TEXTIL",
	);
	const [manualDir, setManualDir] = useState("");
	const [showAdvanced, setShowAdvanced] = useState(false);

	const orcId = pedido?.orc_id ?? parsedOrcId ?? null;
	const oitId = pedido?.oit_id ?? parsedOitId ?? null;

	const previewPath = useMemo(() => {
		if (manualDir.trim()) {
			return normalizeWindowsPath(manualDir.trim());
		}
		const folder = getServiceFolder(serviceId, config);
		return buildContingencyExportPath({
			root: selectedRoot,
			serviceFolder: folder,
			horarioCorte: config.horarioCorte,
		});
	}, [manualDir, selectedRoot, serviceId, config]);

	useEffect(() => {
		if (!isOpen) return;
		setFormat(defaultFormat);
		setIncludeLayoutPng(false);
		setSelectedRoot(getDefaultOfflineRoot());
		setServiceId(inferredServiceId ?? "DTF_TEXTIL");
		setManualDir(
			storage.get<string>(Keys.export.offlineLastManualDir, { area: "local" }) ??
				"",
		);
		setShowAdvanced(false);
	}, [isOpen, defaultFormat, inferredServiceId]);

	if (!isOpen) return null;

	const pedidoLabel =
		orcId && oitId ? `O-${orcId} I-${oitId}` : "Sem pedido em contexto";

	const docLabel = documentLoading
		? "Carregando documento..."
		: documentInfo?.nomeArquivo ?? "—";

	const handleConfirm = () => {
		const outputDir = previewPath;
		if (!outputDir) return;

		if (manualDir.trim()) {
			storage.set(Keys.export.offlineLastManualDir, manualDir.trim(), {
				area: "local",
			});
		} else {
			persistOfflineRoot(selectedRoot);
		}

		onConfirm({
			format,
			includeLayoutPng,
			outputDir,
			root: selectedRoot,
			serviceId,
			manualDir: manualDir.trim() || undefined,
			orcId: orcId ?? undefined,
			oitId: oitId ?? undefined,
		});
	};

	return (
		<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
			<div className="bg-navy-800 border border-navy-700 rounded-lg p-4 w-[520px] max-w-[95vw] max-h-[90vh] overflow-y-auto">
				<div className="flex items-center gap-3 mb-3">
					<div className="p-2 bg-amber-500/15 rounded-lg">
						<FolderDown className="w-5 h-5 text-amber-400" />
					</div>
					<div>
						<div className="text-sm text-gray-100 font-medium">
							Exportar Produção (Offline)
						</div>
						<div className="text-xs text-gray-400">
							Pedido: {pedidoLabel} · Doc: {docLabel}
						</div>
					</div>
				</div>

				<div className="flex items-start gap-2 mb-3 p-2 rounded border border-amber-500/30 bg-amber-500/10 text-xs text-amber-100">
					<AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
					<span>
						Ferramenta de contingência — use apenas quando o export normal falhar.
						Não substitui o fluxo Pedidos → Exportar.
					</span>
				</div>

				<div className="space-y-3 text-xs">
					<div>
						<label className="block text-gray-400 mb-1">Raiz de produção</label>
						<select
							value={selectedRoot}
							onChange={(e) => setSelectedRoot(e.target.value)}
							className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-gray-100"
							disabled={isExporting || !!manualDir.trim()}
						>
							{availableRoots.map((root) => (
								<option key={root} value={root}>
									{root}
								</option>
							))}
						</select>
					</div>

					<div>
						<label className="block text-gray-400 mb-1">
							Serviço
							{parsedMaterial ? (
								<span className="text-gray-500 ml-1">
									(material: {parsedMaterial})
								</span>
							) : null}
						</label>
						<select
							value={serviceId}
							onChange={(e) =>
								setServiceId(e.target.value as OfflineServiceId)
							}
							className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-gray-100"
							disabled={isExporting || !!manualDir.trim()}
						>
							{serviceOptions.map((opt) => (
								<option key={opt.id} value={opt.id}>
									{opt.label}
								</option>
							))}
						</select>
					</div>

					<div>
						<label className="block text-gray-400 mb-1">Pasta destino (preview)</label>
						<div className="bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-gray-200 break-all min-h-[2.5rem]">
							{documentLoading ? (
								<span className="flex items-center gap-2 text-gray-400">
									<Loader2 className="w-3 h-3 animate-spin" />
									Aguardando documento...
								</span>
							) : (
								previewPath
							)}
						</div>
					</div>

					<button
						type="button"
						onClick={() => setShowAdvanced((v) => !v)}
						className="flex items-center gap-1 text-gray-400 hover:text-gray-200"
						disabled={isExporting}
					>
						{showAdvanced ? (
							<ChevronUp className="w-3 h-3" />
						) : (
							<ChevronDown className="w-3 h-3" />
						)}
						Opções avançadas
					</button>

					{showAdvanced && (
						<div className="space-y-3 pl-1 border-l border-navy-600 ml-1">
							<div>
								<label className="block text-gray-400 mb-1">Formato</label>
								<select
									value={format}
									onChange={(e) =>
										setFormat(e.target.value as ExportFormat)
									}
									className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-gray-100"
									disabled={isExporting}
								>
									{FORMAT_OPTIONS.map((opt) => (
										<option key={opt.value} value={opt.value}>
											{opt.label}
										</option>
									))}
								</select>
							</div>

							<label className="flex items-center gap-2 text-gray-300 cursor-pointer">
								<input
									type="checkbox"
									checked={includeLayoutPng}
									onChange={(e) => setIncludeLayoutPng(e.target.checked)}
									disabled={isExporting}
									className="rounded"
								/>
								Incluir layout PNG (opcional)
							</label>

							<div>
								<label className="block text-gray-400 mb-1">
									Pasta manual (override)
								</label>
								<input
									type="text"
									value={manualDir}
									onChange={(e) => setManualDir(e.target.value)}
									placeholder="\\\\servidor\\pasta\\destino"
									className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-gray-100"
									disabled={isExporting}
								/>
								{manualDir.trim() ? (
									<p className="text-amber-400 mt-1">
										Pasta manual — pode não seguir o padrão de produção.
									</p>
								) : null}
							</div>
						</div>
					)}
				</div>

				<div className="flex justify-end gap-2 mt-4">
					<button
						type="button"
						onClick={onClose}
						disabled={isExporting}
						className="px-3 py-1.5 text-xs rounded border border-navy-600 text-gray-300 hover:bg-navy-700"
					>
						Cancelar
					</button>
					<button
						type="button"
						onClick={handleConfirm}
						disabled={isExporting || documentLoading || !previewPath}
						className="px-3 py-1.5 text-xs rounded bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50"
					>
						{isExporting ? "Exportando..." : "Exportar"}
					</button>
				</div>
			</div>
		</div>
	);
}
